import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { UserCircle, CheckCircle2, XCircle } from 'lucide-react';
import PageShell from '../components/layout/PageShell';
import { useProfile } from '../components/ProfileNudgeProvider';
import { profileCompleteness } from '../hooks/useProfileNudge';

const API_URL = process.env.REACT_APP_API_URL;

const personas = ['Seller', 'PTAM', 'PS Seller'];
const segments = ['SMB', 'Commercial', 'Enterprise', 'Strategic', 'Key'];
const alignments = ['Okta WIC', 'Okta CIS', 'Auth0', 'Multi-product'];

const styles = {
  avatar: {
    width: '56px', height: '56px', borderRadius: '50%',
    background: '#e0e7ff', display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '20px', marginBottom: '12px',
  },
  name: { fontSize: '16px', fontWeight: '600' },
  email: { fontSize: '13px', color: '#666', marginBottom: '16px' },
  progressLabel: {
    display: 'flex', justifyContent: 'space-between', fontSize: '12px',
    color: '#666', marginBottom: '6px',
  },
  progressWrap: { height: '6px', background: '#e5e5e5', borderRadius: '3px', width: '100%', marginBottom: '24px' },
  progressBar: (pct) => ({
    height: '6px', borderRadius: '3px', width: `${pct}%`, transition: 'width 0.4s',
    background: pct === 100 ? '#1baf7a' : '#fd5f0d',
  }),
  divider: { borderTop: '1px solid #e5e5e5', margin: '20px 0' },
  label: { fontSize: '13px', fontWeight: '600', marginBottom: '8px', display: 'block' },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '4px',
    fontSize: '13px', marginBottom: '14px', boxSizing: 'border-box',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' },
  chip: (selected) => ({
    padding: '6px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer',
    border: `1px solid ${selected ? '#fd5f0d' : '#ddd'}`,
    background: selected ? '#fef0e6' : '#fff',
    color: selected ? '#fd5f0d' : '#555',
  }),
  saveBtn: {
    padding: '10px 24px', background: '#fd5f0d', color: '#fff',
    border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
  },
  note: { fontSize: '12px', color: '#999', marginTop: '8px' },
  successBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 14px', background: '#ebfbee', border: '1px solid #b2f2bb',
    borderRadius: '6px', color: '#2f9e44', fontSize: '13px', marginBottom: '16px',
  },
  errorBanner: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '10px 14px', background: '#fdecea', border: '1px solid #f5c2be',
    borderRadius: '6px', color: '#b00020', fontSize: '13px', marginBottom: '16px',
  },
};

function Profile() {
  const { user, getAccessTokenSilently } = useAuth0();
  const { profile, updateProfile } = useProfile();

  // Fields collected by the profiling prompts (useProfileNudge.js FIELDS)
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  // Persona chips — optional, saved alongside the profiling-prompt fields
  const [persona, setPersona] = useState('');
  const [segment, setSegment] = useState('');
  const [alignment, setAlignment] = useState('');

  useEffect(() => {
    if (!profile) return;
    setFullName(profile.fullName || '');
    setRole(profile.role || '');
    setPhone(profile.phone || '');
    setPersona(profile.persona || '');
    setSegment(profile.segment || '');
    setAlignment(profile.productAlignment || '');
  }, [profile]);

  const pct = profileCompleteness(profile);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback({ type: '', msg: '' });
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
      });
      const updates = {
        fullName: fullName.trim(), role: role.trim(), phone: phone.trim(),
        persona, segment, productAlignment: alignment,
      };
      const res = await fetch(`${API_URL}api/profile/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save profile.');
      updateProfile(updates);
      setFeedback({ type: 'success', msg: 'Profile updated.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell title="My Profile" desc="Update your persona to get better routing and recommendations.">
      <div style={styles.avatar}><UserCircle size={28} strokeWidth={1.5} style={{ color: '#556' }} /></div>
      <div style={styles.name}>{user?.name || '—'}</div>
      <div style={styles.email}>{profile?.email || user?.email || '—'}</div>

      <div style={styles.progressLabel}>
        <span>Profile completeness</span>
        <span>{pct}%</span>
      </div>
      <div style={styles.progressWrap}><div style={styles.progressBar(pct)} /></div>

      {feedback.msg && (
        <div style={feedback.type === 'success' ? styles.successBanner : styles.errorBanner}>
          {feedback.type === 'success' ? <CheckCircle2 size={14} /> : <XCircle size={14} />} {feedback.msg}
        </div>
      )}

      <form onSubmit={handleSave}>
        <span style={styles.label}>Full Name</span>
        <input
          style={styles.input} type="text" placeholder="e.g. Jane Smith"
          value={fullName} onChange={e => setFullName(e.target.value)}
        />

        <span style={styles.label}>Role / Title</span>
        <input
          style={styles.input} type="text" placeholder="e.g. Solutions Engineer"
          value={role} onChange={e => setRole(e.target.value)}
        />

        <span style={styles.label}>Phone Number</span>
        <input
          style={styles.input} type="tel" placeholder="e.g. +1 (555) 000-0000"
          value={phone} onChange={e => setPhone(e.target.value)}
        />

        <div style={styles.divider} />

        <span style={styles.label}>Persona <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></span>
        <div style={styles.chips}>
          {personas.map(r => (
            <button type="button" key={r} style={styles.chip(persona === r)} onClick={() => setPersona(r)}>{r}</button>
          ))}
        </div>

        <span style={styles.label}>Segment <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></span>
        <div style={styles.chips}>
          {segments.map(s => (
            <button type="button" key={s} style={styles.chip(segment === s)} onClick={() => setSegment(s)}>{s}</button>
          ))}
        </div>

        <span style={styles.label}>Product Alignment <span style={{ color: '#999', fontWeight: 400 }}>(optional)</span></span>
        <div style={styles.chips}>
          {alignments.map(a => (
            <button type="button" key={a} style={styles.chip(alignment === a)} onClick={() => setAlignment(a)}>{a}</button>
          ))}
        </div>

        <button type="submit" style={styles.saveBtn} disabled={saving}>
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
        <div style={styles.note}>Persona, segment, and product alignment are optional and help us route you faster.</div>
      </form>
    </PageShell>
  );
}

export default Profile;
